import { CornerDownLeft, Search, X } from "lucide-react";
import { useEffect, useReducer, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { useNavigate } from "react-router-dom";

import { Status } from "@/shared/components/Status";
import { getApiErrorMessage } from "@/shared/lib/apiError";

import {
  enterTarget,
  flattenHits,
  initialPaletteState,
  nonEmptyGroups,
  paletteReducer,
} from "./paletteReducer";
import { closePalette, usePaletteStore } from "./paletteStore";
import {
  MIN_QUERY_LENGTH,
  fetchSearch,
  readRecent,
  rememberRecent,
  searchResultsUrl,
  type SearchHit,
} from "./searchService";

const DEBOUNCE_MS = 250;

/**
 * Ctrl+K palette (Module 25): type two characters, see quick hits per record
 * type, move with the arrow keys, Enter opens the highlighted record or the
 * full results page, Escape closes.
 */
export default function SearchPalette() {
  const { open, initialQuery } = usePaletteStore();
  if (!open) return null;
  return <PaletteDialog key={initialQuery} initialQuery={initialQuery} />;
}

function PaletteDialog({ initialQuery }: { initialQuery: string }) {
  const navigate = useNavigate();
  const [state, dispatch] = useReducer(paletteReducer, initialQuery, initialPaletteState);
  const [recent, setRecent] = useState<string[]>(() => readRecent());
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const term = state.query.trim();

  useEffect(() => {
    if (term.length < MIN_QUERY_LENGTH) return;
    const timer = window.setTimeout(async () => {
      dispatch({ type: "loading" });
      try {
        const result = await fetchSearch(term);
        dispatch({ type: "results", query: term, groups: result?.groups ?? [] });
      } catch (error) {
        dispatch({
          type: "error",
          message: getApiErrorMessage(error, "Search is not available right now."),
        });
      }
    }, DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
  }, [term]);

  useEffect(() => {
    const el = listRef.current?.querySelector<HTMLElement>('[data-highlighted="true"]');
    el?.scrollIntoView({ block: "nearest" });
  }, [state.highlighted]);

  const go = (hit: SearchHit) => {
    setRecent(rememberRecent(term));
    closePalette();
    navigate(hit.link);
  };

  const goResults = (query: string) => {
    setRecent(rememberRecent(query));
    closePalette();
    navigate(searchResultsUrl(query));
  };

  const onKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === "ArrowDown") {
      event.preventDefault();
      dispatch({ type: "move", delta: 1 });
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      dispatch({ type: "move", delta: -1 });
    } else if (event.key === "Enter") {
      event.preventDefault();
      const target = enterTarget(state);
      if (target?.kind === "hit") go(target.hit);
      else if (target?.kind === "results") goResults(target.query);
    } else if (event.key === "Escape") {
      event.preventDefault();
      closePalette();
    }
  };

  const hits = flattenHits(state.groups);
  const groups = nonEmptyGroups(state.groups);
  const totalAcross = state.groups.reduce((sum, g) => sum + g.total, 0);
  let runningIndex = -1;

  return ReactDOM.createPortal(
    <div
      className="fixed inset-0 z-[80] flex items-start justify-center bg-black/30 px-4 pt-[10vh] backdrop-blur-[1px]"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) closePalette();
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Search"
        className="w-full max-w-2xl overflow-hidden rounded-2xl border border-theme-border bg-theme-surface shadow-2xl"
      >
        <div className="flex items-center gap-3 border-b border-theme-border px-4">
          <Search className="h-4 w-4 shrink-0 text-theme-text-soft" />
          <input
            ref={inputRef}
            autoFocus
            value={state.query}
            onChange={(e) => dispatch({ type: "query", query: e.target.value })}
            onKeyDown={onKeyDown}
            placeholder="Search clients, opportunities, submissions, policies, partners, users…"
            aria-label="Search"
            aria-controls="search-palette-results"
            aria-activedescendant={
              state.highlighted >= 0 ? `search-hit-${state.highlighted}` : undefined
            }
            className="h-12 w-full bg-transparent text-sm text-theme-text outline-none placeholder:text-theme-text-placeholder"
          />
          {state.loading ? <span className="text-xs text-theme-text-soft">Searching…</span> : null}
          <button
            type="button"
            aria-label="Close search"
            onClick={closePalette}
            className="rounded p-1 text-theme-text-soft hover:text-theme-text"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        <div
          ref={listRef}
          id="search-palette-results"
          role="listbox"
          className="thin-scrollbar max-h-[55vh] overflow-y-auto"
        >
          {term.length < MIN_QUERY_LENGTH ? (
            <div className="px-4 py-3 text-xs text-theme-text-soft">
              {recent.length ? (
                <>
                  <div className="mb-1 font-semibold uppercase tracking-wide">Recent</div>
                  <ul className="space-y-0.5">
                    {recent.map((q) => (
                      <li key={q}>
                        <button
                          type="button"
                          className="w-full rounded px-2 py-1 text-left text-sm text-theme-text hover:bg-theme-surface-soft"
                          onClick={() => dispatch({ type: "query", query: q })}
                        >
                          {q}
                        </button>
                      </li>
                    ))}
                  </ul>
                </>
              ) : (
                <>
                  Type at least {MIN_QUERY_LENGTH} characters. Results show only what you may open.
                </>
              )}
            </div>
          ) : state.error ? (
            <div className="px-4 py-3 text-sm text-theme-danger">{state.error}</div>
          ) : !state.loading && hits.length === 0 ? (
            <div className="px-4 py-3 text-sm text-theme-text-soft">Nothing matches “{term}”.</div>
          ) : (
            groups.map((group) => (
              <div key={group.key} role="group" aria-label={group.label}>
                <div className="flex items-center justify-between px-4 pt-2 text-[11px] font-semibold uppercase tracking-wide text-theme-text-soft">
                  <span>{group.label}</span>
                  {group.total > group.hits.length ? (
                    <button
                      type="button"
                      className="normal-case text-theme-accent hover:underline"
                      onClick={() => {
                        closePalette();
                        navigate(searchResultsUrl(term, group.key));
                      }}
                    >
                      all {group.total}
                    </button>
                  ) : null}
                </div>
                <ul className="py-1">
                  {group.hits.map((hit) => {
                    runningIndex += 1;
                    const index = runningIndex;
                    const highlighted = state.highlighted === index;
                    return (
                      <li key={`${hit.group}-${hit.id}`}>
                        <button
                          type="button"
                          id={`search-hit-${index}`}
                          role="option"
                          aria-selected={highlighted}
                          data-highlighted={highlighted ? "true" : undefined}
                          onMouseEnter={() => dispatch({ type: "highlight", index })}
                          onClick={() => go(hit)}
                          className={`flex w-full items-center gap-3 px-4 py-2 text-left ${highlighted ? "bg-theme-accent-soft/40" : "hover:bg-theme-surface-soft"}`}
                        >
                          <span className="min-w-0 flex-1">
                            <span className="block truncate text-sm font-medium text-theme-text">
                              {hit.title}
                            </span>
                            {hit.subtitle ? (
                              <span className="block truncate text-xs text-theme-text-soft">
                                {hit.subtitle}
                              </span>
                            ) : null}
                          </span>
                          {hit.status ? <Status status={hit.status} /> : null}
                          {highlighted ? (
                            <CornerDownLeft
                              className="h-3.5 w-3.5 shrink-0 text-theme-text-placeholder"
                              aria-hidden
                            />
                          ) : null}
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </div>
            ))
          )}
        </div>
        <div className="flex items-center justify-between border-t border-theme-border px-4 py-2 text-[11px] text-theme-text-soft">
          <span>
            <kbd className="rounded border border-theme-border px-1">↑</kbd>{" "}
            <kbd className="rounded border border-theme-border px-1">↓</kbd> move ·{" "}
            <kbd className="rounded border border-theme-border px-1">Enter</kbd> open ·{" "}
            <kbd className="rounded border border-theme-border px-1">Esc</kbd> close
          </span>
          {term.length >= MIN_QUERY_LENGTH && totalAcross > 0 ? (
            <button
              type="button"
              className="text-theme-accent hover:underline"
              onClick={() => goResults(term)}
            >
              All {totalAcross} result{totalAcross === 1 ? "" : "s"}
            </button>
          ) : null}
        </div>
      </div>
    </div>,
    document.body
  );
}
